import type { JSX } from 'react';
import { useState } from 'react';
import { SafeHtml } from '../components/SafeHtml';
import {
  ArrowRightOutlined,
  CalendarOutlined,
  CheckCircleFilled,
  ClockCircleOutlined,
  CommentOutlined,
  DownloadOutlined,
  ExclamationCircleFilled,
  FileExcelOutlined,
  FileImageOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  HomeOutlined,
  MoreOutlined,
  RocketOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Button,
  DatePicker,
  Empty,
  App,
  Modal,
  Progress,
  Skeleton,
  Tabs,
  Tag,
  Tooltip,
} from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { authApi } from '../lib/authApi';
import { friendlyError } from '../lib/api';
import { openingApi, wikiApi, type MyOpeningProgressDto, type OpeningStepDto } from '../lib/wikiApi';
import { isNotForViewer } from '../components/opening-tasks/opening-query';
import { OpeningManagerScreen } from '../components/opening-plan/OpeningManagerScreen';
import { OpeningPlanView } from '../components/opening-plan/OpeningPlanView';
import { useOpeningPlan } from '../components/opening-plan/useOpeningPlan';
import dayjs from 'dayjs';

/**
 * Страница «Открытие точки» (/opening). Что показать, решается по порядку:
 *   1. у партнёра есть карточка открытия (владелец 31.10.2026) — визард по плану карточки, как в CRM, но для чтения:
 *      обзор, диаграмма Ганта, задачи, чеклисты, обращения (components/opening-plan, `GET /wiki/opening/plan`);
 *   2. иначе, если у сотрудника УК есть право «Этапы открытия — просмотр» (`me.canViewOpenings`), — экран менеджера:
 *      список открытий партнёров и их планы (`?card=`), только чтение (`GET /wiki/opening/plans`);
 *   3. иначе — прежний экран ниже: «Начать открытие» и чек-лист из шагов на `/wiki/opening/me`.
 * Если раздел не про вас (401/403/404 на плане) — сразу дальше по списку; сбой сервера или сети — «Повторить».
 */
export function OpeningPage(): JSX.Element {
  const plan = useOpeningPlan();
  // Общий с остальным хабом запрос (ключ ['wiki-auth','me']): обычно уже в кеше.
  const me = useQuery({
    queryKey: ['wiki-auth', 'me'],
    queryFn: () => authApi.me(),
    retry: false,
    staleTime: 60_000,
  });

  if (plan.isLoading) {
    return <Skeleton active paragraph={{ rows: 8 }} />;
  }
  if (plan.data?.card) {
    return <OpeningPlanView card={plan.data.card} />;
  }
  // Сбой сервера или сети — не путаем с «карточки нет»: даём повторить.
  if (plan.error && !isNotForViewer(plan.error)) {
    return (
      <div>
        <h1 className="fk-page-title">Открытие точки</h1>
        <Alert
          type="error"
          showIcon
          message="Не удалось загрузить открытие точки"
          description={friendlyError(plan.error)}
          style={{ marginTop: 16 }}
          action={
            <Button size="small" onClick={() => void plan.refetch()} loading={plan.isFetching}>
              Повторить
            </Button>
          }
        />
      </div>
    );
  }
  if (me.isLoading) {
    return <Skeleton active paragraph={{ rows: 8 }} />;
  }
  if (me.data?.canViewOpenings) {
    return <OpeningManagerScreen />;
  }
  return <LegacyOpeningPage />;
}

/**
 * Прежний экран «Открытие точки» — wizard с прогрессом точки конкретного
 * партнёра (для тех, у кого нет карточки открытия). Использует реальные backend endpoints из wiki-opening.service:
 *
 *   - GET  /wiki/opening/me                                  — мой прогресс
 *   - POST /wiki/opening/me                                  — стартовать
 *   - PATCH /wiki/opening/me/date                            — предложить дату
 *   - PATCH /wiki/opening/me/items/:slug/:key                — отметить пункт
 *
 * Если у юзера есть partnerId и OpeningProgress отсутствует — показываем
 * экран «Начать открытие» с DatePicker. Если есть — полноценный wizard.
 * Если юзер не партнёр или сотрудник iiko — empty-state с подсказкой.
 *
 * Стиль из Figma-мокапа: карточка точки + 3 inline-карточки + 2 колонки
 * (текущий шаг с табами + список 15 шагов) + материалы + «УК видит».
 */
function LegacyOpeningPage(): JSX.Element {
  const meQuery = useQuery({
    queryKey: ['wiki-auth', 'me'],
    queryFn: () => authApi.me(),
    retry: false,
    staleTime: 60_000,
  });

  const openingQuery = useQuery({
    queryKey: ['wiki', 'opening', 'me'],
    queryFn: () => openingApi.getMine(),
    retry: false,
  });

  if (meQuery.isLoading || openingQuery.isLoading) {
    return <Skeleton active paragraph={{ rows: 8 }} />;
  }
  if (meQuery.error) {
    return (
      <Alert
        type="error"
        showIcon
        message="Не удалось загрузить"
        description={(meQuery.error as Error).message}
      />
    );
  }

  // Если юзер не привязан к partner — wizard недоступен.
  if (!openingQuery.data?.hasPartner) {
    return <NotAPartnerEmptyState />;
  }

  // Если progress нет — экран «Начать открытие точки».
  if (!openingQuery.data.progress) {
    return <StartOpeningEmptyState />;
  }

  const isCrm = meQuery.data?.type === 'crm_user';

  return <OpeningWizardView progress={openingQuery.data.progress} isCrmUser={isCrm} />;
}

// ─── Empty states ─────────────────────────────────────────────────

function NotAPartnerEmptyState(): JSX.Element {
  return (
    <div>
      <h1 className="fk-page-title">Открытие точки</h1>
      <Alert
        type="info"
        showIcon
        message="Wizard открытия доступен только франчайзи-партнёрам"
        description="Если вы — сотрудник партнёра, обратитесь к партнёру за статусом открытия. Открытия партнёров видят сотрудники УК с правом «Этапы открытия — просмотр» — его выдают в CRM в разделе «Роли»."
        style={{ marginTop: 16 }}
      />
    </div>
  );
}

function StartOpeningEmptyState(): JSX.Element {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState<dayjs.Dayjs | null>(null);

  const startMutation = useMutation({
    mutationFn: () => openingApi.start(date?.format('YYYY-MM-DD')),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['wiki', 'opening', 'me'] });
      void message.success('Открытие точки запущено!');
      setOpen(false);
    },
    onError: (err: Error) => void message.error(err.message),
  });

  return (
    <div>
      <h1 className="fk-page-title">Открытие точки</h1>
      <p className="fk-page-sub" style={{ marginBottom: 24 }}>
        Пошаговый wizard поможет открыть точку за 45–60 дней — от регистрации ИП
        до торжественного открытия.
      </p>

      <div
        style={{
          background: 'var(--fk-surface)',
          border: '1px solid var(--fk-border)',
          borderRadius: 8,
          padding: 32,
          textAlign: 'center',
        }}
      >
        <RocketOutlined
          style={{
            fontSize: 56,
            color: 'var(--fk-orange)',
            marginBottom: 16,
          }}
        />
        <h2 style={{ fontSize: 20, fontWeight: 700, color: 'var(--fk-indigo)' }}>
          Начнём открывать точку?
        </h2>
        <p
          style={{
            color: 'var(--fk-text-2)',
            maxWidth: 480,
            margin: '0 auto 20px',
          }}
        >
          После старта вы получите чек-лист из 7 шагов: от регистрации ИП до открытия.
          Прогресс виден УК — куратор подключится сам, как только будет нужно.
        </p>
        <Button
          type="primary"
          size="large"
          icon={<ArrowRightOutlined />}
          style={{
            background: 'var(--fk-orange)',
            borderColor: 'var(--fk-orange)',
            fontWeight: 600,
          }}
          onClick={() => setOpen(true)}
        >
          Запустить wizard
        </Button>
      </div>

      <Modal
        title="Когда планируете открыться?"
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => startMutation.mutate()}
        confirmLoading={startMutation.isPending}
        okText="Поехали"
        cancelText="Отмена"
        okButtonProps={{
          style: { background: 'var(--fk-orange)', borderColor: 'var(--fk-orange)' },
        }}
      >
        <p style={{ color: 'var(--fk-text-2)', marginBottom: 12 }}>
          Дату можно изменить в любой момент. УК потом подтвердит её и зафиксирует.
        </p>
        <DatePicker
          value={date}
          onChange={(d) => setDate(d)}
          format="DD.MM.YYYY"
          style={{ width: '100%' }}
          placeholder="Выберите дату открытия"
          disabledDate={(d) => d && d < dayjs().add(7, 'day')}
        />
      </Modal>
    </div>
  );
}

// ─── Wizard view (есть прогресс) ──────────────────────────────────

function OpeningWizardView({
  progress,
  isCrmUser,
}: {
  progress: MyOpeningProgressDto;
  isCrmUser: boolean;
}): JSX.Element {
  const articlesQuery = useQuery({
    queryKey: ['wiki', 'articles', { blockCode: 'opening', limit: 8 }],
    queryFn: () => wikiApi.listArticles({ blockCode: 'opening', limit: 8 }),
  });

  const targetDate = progress.confirmedOpenDate ?? progress.proposedOpenDate;
  const overdueCount = progress.overdueCount;
  const overdueDays =
    progress.daysLeft !== null && progress.daysLeft < 0 ? -progress.daysLeft : 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Хлебная крошка */}
      <div style={{ fontSize: 13, color: 'var(--fk-text-muted)' }}>
        <Link to="/" style={{ color: 'inherit' }}>
          <HomeOutlined /> Главная
        </Link>
        {' / '}
        <span style={{ color: 'var(--fk-text-2)' }}>Открытие точки</span>
      </div>

      <div>
        <h1 className="fk-page-title" style={{ margin: 0, marginBottom: 4 }}>Открытие точки</h1>
        <p className="fk-page-sub" style={{ margin: 0 }}>Пошаговый запуск новой точки: от регистрации ИП до открытия</p>
      </div>

      <PointProgressCard progress={progress} targetDate={targetDate} />

      <InlineStatsCards
        progress={progress}
        overdueCount={overdueCount}
        overdueDays={overdueDays}
      />

      <div className="opening-grid">
        <CurrentStepCard progress={progress} />
        <StepsListCard steps={progress.steps} totalSteps={progress.totalSteps} />
        <style>{`
          .opening-grid {
            display: grid;
            grid-template-columns: 1fr;
            gap: 20px;
          }
          @media (min-width: 1100px) {
            .opening-grid {
              grid-template-columns: minmax(0, 2fr) minmax(320px, 1fr);
            }
          }
        `}</style>
      </div>

      <div className="opening-grid-bottom">
        <MaterialsCard
          isLoading={articlesQuery.isLoading}
          items={(articlesQuery.data?.items ?? []).slice(0, 4)}
        />
        {isCrmUser && <UkSeesCard progress={progress} overdueDays={overdueDays} />}
        <style>{`
          .opening-grid-bottom {
            display: grid;
            grid-template-columns: 1fr;
            gap: 20px;
          }
          @media (min-width: 1100px) {
            .opening-grid-bottom {
              grid-template-columns: minmax(0, 2fr) minmax(320px, 1fr);
            }
          }
        `}</style>
      </div>
    </div>
  );
}

// ─── Карточка точки с прогрессом ─────────────────────────────────

function PointProgressCard({
  progress,
  targetDate,
}: {
  progress: MyOpeningProgressDto;
  targetDate: string | null;
}): JSX.Element {
  const isOverdue = progress.daysLeft !== null && progress.daysLeft < 0;
  const status = progress.completedAt
    ? 'Открыта'
    : isOverdue
      ? 'Просрочена'
      : 'В работе';
  const statusColor: 'orange' | 'green' | 'red' = progress.completedAt
    ? 'green'
    : isOverdue
      ? 'red'
      : 'orange';

  return (
    <div
      style={{
        background: 'var(--fk-surface)',
        border: '1px solid var(--fk-border)',
        borderRadius: 8,
        padding: 20,
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 16,
        }}
      >
        <div style={{ flex: '1 1 240px', minWidth: 240 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              marginBottom: 6,
            }}
          >
            <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--fk-indigo)' }}>
              Моя точка
            </div>
            <Tag color={statusColor} style={{ margin: 0, fontWeight: 600 }}>
              {status}
            </Tag>
          </div>
          <div style={{ fontSize: 14, color: 'var(--fk-text-2)' }}>
            Шаг {progress.currentStepIndex} из {progress.totalSteps} —{' '}
            <strong>{progress.currentStepTitle}</strong>
          </div>
        </div>

        <div style={{ flex: '1 1 200px', minWidth: 200 }}>
          <Progress
            percent={progress.progressPercent}
            strokeColor="var(--fk-orange)"
            showInfo={false}
          />
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: 12,
              color: 'var(--fk-text-muted)',
              marginTop: 4,
            }}
          >
            <span>
              <CalendarOutlined />{' '}
              {targetDate
                ? `План: ${dayjs(targetDate).format('DD MMM YYYY')}`
                : 'Дата не задана'}
            </span>
            <span>{progress.progressPercent}%</span>
          </div>
        </div>

        <div
          style={{
            flex: '0 0 auto',
            display: 'flex',
            gap: 8,
            alignItems: 'center',
          }}
        >
          {progress.currentStepSlug && (
            <Link to={`/article/${progress.currentStepSlug}`}>
              <Button
                type="primary"
                size="large"
                icon={<ArrowRightOutlined />}
                style={{
                  background: 'var(--fk-orange)',
                  borderColor: 'var(--fk-orange)',
                  fontWeight: 600,
                }}
              >
                Продолжить шаг
              </Button>
            </Link>
          )}
          <Tooltip title="Комментарии (скоро)">
            <Button icon={<CommentOutlined />} size="large" />
          </Tooltip>
          <Tooltip title="Документы (скоро)">
            <Button icon={<FileTextOutlined />} size="large" />
          </Tooltip>
          <Tooltip title="Ещё">
            <Button icon={<MoreOutlined />} size="large" />
          </Tooltip>
        </div>
      </div>
    </div>
  );
}

// ─── 3 inline-карточки ─────────────────────────────────────────────

function InlineStatsCards({
  progress,
  overdueCount,
  overdueDays,
}: {
  progress: MyOpeningProgressDto;
  overdueCount: number;
  overdueDays: number;
}): JSX.Element {
  const targetDate = progress.confirmedOpenDate ?? progress.proposedOpenDate;
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: 12,
      }}
    >
      <InlineStatCard
        icon={<CalendarOutlined />}
        iconBg="rgba(255, 105, 0, 0.10)"
        iconColor="var(--fk-orange)"
        label="Срок запуска"
        value={
          targetDate ? dayjs(targetDate).format('DD MMM YYYY') : 'Не задан'
        }
        sub={
          progress.daysLeft === null
            ? 'Уточните дату'
            : progress.daysLeft >= 0
              ? `Осталось ${progress.daysLeft} ${pluralizeDays(progress.daysLeft)}`
              : `Просрочено на ${-progress.daysLeft} ${pluralizeDays(-progress.daysLeft)}`
        }
      />
      <InlineStatCard
        icon={<UserOutlined />}
        iconBg="rgba(0, 150, 255, 0.10)"
        iconColor="var(--fk-info)"
        label="Шагов выполнено"
        value={`${progress.completedSteps} из ${progress.totalSteps}`}
        sub={`${progress.progressPercent}% готовности`}
      />
      <InlineStatCard
        icon={<ClockCircleOutlined />}
        iconBg={overdueCount > 0 ? 'rgba(229, 57, 53, 0.10)' : 'rgba(124, 179, 66, 0.10)'}
        iconColor={overdueCount > 0 ? 'var(--fk-error)' : 'var(--fk-success)'}
        label="Просрочено задач"
        value={overdueCount === 0 ? 'Нет' : `${overdueCount}`}
        sub={overdueCount === 0 ? 'Всё по плану' : `На ${overdueDays} ${pluralizeDays(overdueDays)}`}
      />
    </div>
  );
}

function InlineStatCard({
  icon,
  iconBg,
  iconColor,
  label,
  value,
  sub,
}: {
  icon: JSX.Element;
  iconBg: string;
  iconColor: string;
  label: string;
  value: string;
  sub: string;
}): JSX.Element {
  return (
    <div
      style={{
        background: 'var(--fk-surface)',
        border: '1px solid var(--fk-border)',
        borderRadius: 8,
        padding: 16,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
      }}
    >
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: 6,
          background: iconBg,
          color: iconColor,
          display: 'grid',
          placeItems: 'center',
          fontSize: 20,
          flexShrink: 0,
        }}
      >
        {icon}
      </div>
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: 12,
            color: 'var(--fk-text-muted)',
            marginBottom: 2,
          }}
        >
          {label}
        </div>
        <div
          style={{
            fontSize: 16,
            fontWeight: 700,
            color: 'var(--fk-text)',
            lineHeight: 1.2,
          }}
        >
          {value}
        </div>
        <div style={{ fontSize: 12, color: 'var(--fk-text-muted)' }}>{sub}</div>
      </div>
    </div>
  );
}

// ─── Текущий шаг (4 таба) ──────────────────────────────────────────

function CurrentStepCard({ progress }: { progress: MyOpeningProgressDto }): JSX.Element {
  const [tab, setTab] = useState<'guide' | 'checklist' | 'files' | 'comments'>(
    'guide',
  );
  const current =
    progress.steps.find((s) => s.status === 'in_progress' || s.status === 'overdue') ??
    progress.steps[progress.currentStepIndex - 1] ??
    progress.steps[0];

  if (!current) {
    return (
      <div
        style={{
          background: 'var(--fk-surface)',
          border: '1px solid var(--fk-border)',
          borderRadius: 8,
          padding: 20,
        }}
      >
        <Empty description="Нет шагов. УК ещё не опубликовала чек-листы открытия." />
      </div>
    );
  }

  return (
    <div
      style={{
        background: 'var(--fk-surface)',
        border: '1px solid var(--fk-border)',
        borderRadius: 8,
        padding: 20,
      }}
    >
      <h2
        style={{
          fontSize: 18,
          fontWeight: 700,
          color: 'var(--fk-indigo)',
          margin: 0,
          marginBottom: 16,
        }}
      >
        Шаг {current.idx}. {current.title}
      </h2>

      <Tabs
        activeKey={tab}
        onChange={(k) => setTab(k as typeof tab)}
        items={[
          { key: 'guide', label: 'Инструкция' },
          {
            key: 'checklist',
            label: (
              <span>
                Чек-лист{' '}
                {current.totalCount > 0 && (
                  <Tag color="default">{current.doneCount}/{current.totalCount}</Tag>
                )}
              </span>
            ),
          },
          { key: 'files', label: 'Файлы' },
          { key: 'comments', label: 'Комментарии' },
        ]}
      />

      {tab === 'guide' && <StepGuidePanel slug={current.slug} />}
      {tab === 'checklist' && (
        <StepChecklistPanel
          slug={current.slug}
          items={current.items}
        />
      )}
      {tab === 'files' && <StepFilesPanel slug={current.slug} />}
      {tab === 'comments' && <StepCommentsPanel />}
    </div>
  );
}

function StepGuidePanel({ slug }: { slug: string }): JSX.Element {
  const articleQuery = useQuery({
    queryKey: ['wiki', 'article', slug],
    queryFn: () => wikiApi.getArticle(slug),
    retry: false,
  });

  if (articleQuery.isLoading) {
    return <Skeleton active paragraph={{ rows: 4 }} style={{ marginTop: 12 }} />;
  }
  if (articleQuery.error || !articleQuery.data) {
    return (
      <Alert
        type="warning"
        message="Не удалось загрузить инструкцию"
        showIcon
        style={{ marginTop: 12 }}
      />
    );
  }
  return (
    <div style={{ marginTop: 12 }}>
      <SafeHtml className="wiki-content" html={articleQuery.data.bodyHtml} />
      <Link to={`/article/${slug}`}>
        <Button type="link" icon={<ArrowRightOutlined />}>
          Открыть статью полностью
        </Button>
      </Link>
    </div>
  );
}

function StepChecklistPanel({
  slug,
  items,
}: {
  slug: string;
  items: Array<{ key: string; isDone: boolean; doneAt: string | null }>;
}): JSX.Element {
  const { message } = App.useApp();
  const qc = useQueryClient();
  // Если чеклист ещё не сохранён в БД (items.length===0) — показываем 5
  // плейсхолдеров (UI пока без парсинга markdown — D.4.1 добавит реальное
  // извлечение пунктов из статьи). При клике отмечаем сразу в БД.
  const fallbackItems =
    items.length > 0
      ? items
      : [
          { key: 'item-1', isDone: false, doneAt: null },
          { key: 'item-2', isDone: false, doneAt: null },
          { key: 'item-3', isDone: false, doneAt: null },
          { key: 'item-4', isDone: false, doneAt: null },
          { key: 'item-5', isDone: false, doneAt: null },
        ];

  const toggleMutation = useMutation({
    mutationFn: ({ key, isDone }: { key: string; isDone: boolean }) =>
      openingApi.toggleItem(slug, key, isDone),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['wiki', 'opening', 'me'] }),
    onError: (err: Error) => void message.error(err.message),
  });

  return (
    <div style={{ marginTop: 12 }}>
      {items.length === 0 && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 10 }}
          message="Чек-лист пока временный"
          description="Парсер пунктов из markdown добавим в D.4.1. Пока что можно отмечать 5 placeholder-пунктов — отметки сохраняются в БД."
        />
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {fallbackItems.map((item, idx) => (
          <label
            key={item.key}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '8px 10px',
              borderRadius: 8,
              cursor: 'pointer',
              background: item.isDone ? 'rgba(124, 179, 66, 0.06)' : 'var(--fk-bg)',
              border: '1px solid var(--fk-border)',
              transition: 'all 0.1s',
            }}
          >
            <input
              type="checkbox"
              checked={item.isDone}
              disabled={toggleMutation.isPending}
              onChange={() => toggleMutation.mutate({ key: item.key, isDone: !item.isDone })}
              style={{ width: 18, height: 18, accentColor: 'var(--fk-success)' }}
            />
            <span
              style={{
                fontSize: 14,
                color: item.isDone ? 'var(--fk-text-muted)' : 'var(--fk-text)',
                textDecoration: item.isDone ? 'line-through' : 'none',
              }}
            >
              Пункт {idx + 1}
            </span>
          </label>
        ))}
      </div>
    </div>
  );
}

function StepFilesPanel({ slug }: { slug: string }): JSX.Element {
  void slug;
  return (
    <div
      style={{
        marginTop: 12,
        textAlign: 'center',
        padding: 32,
        color: 'var(--fk-text-muted)',
        background: 'var(--fk-bg)',
        borderRadius: 8,
      }}
    >
      <FileTextOutlined style={{ fontSize: 32, marginBottom: 8 }} />
      <div>Прикрепляйте сюда фото, акты и сметы.</div>
      <div style={{ fontSize: 12, marginTop: 4 }}>
        Подключим в D.4.2 (загрузка через chunked-upload).
      </div>
    </div>
  );
}

function StepCommentsPanel(): JSX.Element {
  return (
    <div
      style={{
        marginTop: 12,
        textAlign: 'center',
        padding: 32,
        color: 'var(--fk-text-muted)',
        background: 'var(--fk-bg)',
        borderRadius: 8,
      }}
    >
      <CommentOutlined style={{ fontSize: 32, marginBottom: 8 }} />
      <div>Комментарии шага появятся в E.1.</div>
    </div>
  );
}

// ─── План открытия — все шаги ──────────────────────────────────────

function StepsListCard({
  steps,
  totalSteps,
}: {
  steps: OpeningStepDto[];
  totalSteps: number;
}): JSX.Element {
  return (
    <div
      style={{
        background: 'var(--fk-surface)',
        border: '1px solid var(--fk-border)',
        borderRadius: 8,
        padding: 20,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 14,
        }}
      >
        <h2
          style={{
            fontSize: 16,
            fontWeight: 700,
            color: 'var(--fk-indigo)',
            margin: 0,
          }}
        >
          План открытия
        </h2>
        <Tag style={{ margin: 0 }}>{totalSteps} шагов</Tag>
      </div>

      {steps.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Чек-листы шагов появятся когда УК их опубликует"
        />
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {steps.map((s) => (
              <StepRow key={s.slug} step={s} />
            ))}
          </div>

          <StepGantt steps={steps} />

          <div
            style={{
              marginTop: 12,
              display: 'flex',
              gap: 12,
              flexWrap: 'wrap',
              fontSize: 11,
              color: 'var(--fk-text-muted)',
            }}
          >
            <span>
              <CheckCircleFilled style={{ color: 'var(--fk-success)' }} /> Выполнено
            </span>
            <span>
              <ClockCircleOutlined style={{ color: 'var(--fk-orange)' }} /> В работе
            </span>
            <span style={{ opacity: 0.6 }}>○ Ожидает</span>
            <span>
              <ExclamationCircleFilled style={{ color: 'var(--fk-error)' }} /> Просрочено
            </span>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Простая диаграмма Ганта по этапам открытия (Q17, без frappe-gantt).
 * Длительность полосы ∝ expectedDays этапа, цвет — по статусу. Старт каждой
 * полосы = накопленная сумма дней предыдущих этапов (последовательный план).
 */
function StepGantt({ steps }: { steps: OpeningStepDto[] }): JSX.Element {
  if (steps.length === 0) return <></>;
  const withDays = steps.map((s) => ({
    ...s,
    days: s.expectedDays && s.expectedDays > 0 ? s.expectedDays : 3,
  }));
  const total = withDays.reduce((a, s) => a + s.days, 0) || 1;
  let cum = 0;
  const rows = withDays.map((s) => {
    const startDay = cum;
    cum += s.days;
    return { ...s, startDay };
  });
  const statusColor: Record<OpeningStepDto['status'], string> = {
    completed: 'var(--fk-success)',
    in_progress: 'var(--fk-orange)',
    overdue: 'var(--fk-error)',
    waiting: 'var(--fk-text-muted)',
  };
  return (
    <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px dashed var(--fk-border)' }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--fk-text-muted)', textTransform: 'uppercase', marginBottom: 8 }}>
        Диаграмма Ганта
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {rows.map((s) => (
          <div key={s.slug} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 18, fontSize: 11, color: 'var(--fk-text-muted)', textAlign: 'right', flexShrink: 0 }}>{s.idx}</span>
            <div style={{ position: 'relative', flex: 1, height: 16, background: 'var(--fk-bg)', borderRadius: 4 }}>
              <Tooltip title={`${s.title} · ~${s.days} дн`}>
                <div
                  style={{
                    position: 'absolute',
                    left: `${(s.startDay / total) * 100}%`,
                    width: `${(s.days / total) * 100}%`,
                    height: 16,
                    background: statusColor[s.status],
                    borderRadius: 4,
                    minWidth: 6,
                    opacity: s.status === 'waiting' ? 0.4 : 0.85,
                  }}
                />
              </Tooltip>
            </div>
          </div>
        ))}
      </div>
      <div style={{ fontSize: 11, color: 'var(--fk-text-muted)', marginTop: 6 }}>
        Длительность ∝ плановым дням этапа (~{total} дн всего)
      </div>
    </div>
  );
}

function StepRow({ step }: { step: OpeningStepDto }): JSX.Element {
  const meta: Record<
    typeof step.status,
    { icon: JSX.Element; label: string; color: string }
  > = {
    completed: {
      icon: <CheckCircleFilled />,
      label: 'Выполнено',
      color: 'var(--fk-success)',
    },
    in_progress: {
      icon: <ClockCircleOutlined />,
      label: 'В работе',
      color: 'var(--fk-orange)',
    },
    waiting: {
      icon: (
        <span
          style={{
            display: 'inline-block',
            width: 14,
            height: 14,
            borderRadius: '50%',
            border: '1.5px solid currentColor',
          }}
        />
      ),
      label: 'Ожидает',
      color: 'var(--fk-text-muted)',
    },
    overdue: {
      icon: <ExclamationCircleFilled />,
      label: 'Просрочен',
      color: 'var(--fk-error)',
    },
  };
  const m = meta[step.status];
  const isCurrent = step.status === 'in_progress' || step.status === 'overdue';

  return (
    <Link
      to={`/article/${step.slug}`}
      style={{
        display: 'grid',
        gridTemplateColumns: '22px 24px 1fr auto',
        gap: 8,
        alignItems: 'center',
        padding: '6px 8px',
        borderRadius: 8,
        background: isCurrent ? 'rgba(255, 105, 0, 0.06)' : 'transparent',
        fontSize: 13,
        textDecoration: 'none',
        color: 'inherit',
      }}
    >
      <span
        style={{
          color: m.color,
          fontSize: 14,
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        {m.icon}
      </span>
      <span style={{ color: 'var(--fk-text-muted)', fontSize: 12, textAlign: 'center' }}>
        {step.idx}
      </span>
      <span
        style={{
          color: isCurrent ? 'var(--fk-indigo)' : 'var(--fk-text-2)',
          fontWeight: isCurrent ? 600 : 400,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {step.title}
      </span>
      <span
        style={{
          fontSize: 11,
          color: m.color,
          fontWeight: 600,
        }}
      >
        {m.label}
      </span>
    </Link>
  );
}

// ─── Материалы для шага ────────────────────────────────────────────

function MaterialsCard({
  isLoading,
  items,
}: {
  isLoading: boolean;
  items: Array<{ slug: string; title: string; type: string }>;
}): JSX.Element {
  return (
    <div
      style={{
        background: 'var(--fk-surface)',
        border: '1px solid var(--fk-border)',
        borderRadius: 8,
        padding: 20,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 12,
        }}
      >
        <h2
          style={{
            fontSize: 16,
            fontWeight: 700,
            color: 'var(--fk-indigo)',
            margin: 0,
          }}
        >
          Материалы для шага
        </h2>
        <Link to="/block/opening" style={{ fontSize: 13, color: 'var(--fk-orange)' }}>
          Смотреть все материалы
        </Link>
      </div>

      {isLoading ? (
        <Skeleton active paragraph={{ rows: 2 }} />
      ) : items.length === 0 ? (
        <div
          style={{
            textAlign: 'center',
            color: 'var(--fk-text-muted)',
            fontSize: 13,
            padding: 24,
          }}
        >
          Шаблоны договоров и брендбук появятся когда УК их опубликует.
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 12,
          }}
        >
          {items.map((it) => (
            <MaterialTile key={it.slug} item={it} />
          ))}
        </div>
      )}
    </div>
  );
}

function MaterialTile({
  item,
}: {
  item: { slug: string; title: string; type: string };
}): JSX.Element {
  const { icon, label, color } = guessFileType(item.title, item.type);
  return (
    <Link to={`/article/${item.slug}`} style={{ textDecoration: 'none' }}>
      <div
        style={{
          padding: 14,
          border: '1px solid var(--fk-border)',
          borderRadius: 8,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          minHeight: 100,
          transition: 'all 0.1s',
          cursor: 'pointer',
          background: 'var(--fk-bg)',
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLDivElement).style.borderColor = color;
          (e.currentTarget as HTMLDivElement).style.background = 'var(--fk-surface)';
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--fk-border)';
          (e.currentTarget as HTMLDivElement).style.background = 'var(--fk-bg)';
        }}
      >
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 8,
            background: `${color}15`,
            color,
            display: 'grid',
            placeItems: 'center',
            fontSize: 18,
          }}
        >
          {icon}
        </div>
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: 'var(--fk-text)',
            lineHeight: 1.3,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {item.title}
        </div>
        <div
          style={{
            marginTop: 'auto',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: 11,
            color: 'var(--fk-text-muted)',
          }}
        >
          <span>{label}</span>
          <DownloadOutlined style={{ color: 'var(--fk-text-muted)' }} />
        </div>
      </div>
    </Link>
  );
}

function guessFileType(
  title: string,
  _articleType: string,
): { icon: JSX.Element; label: string; color: string } {
  const low = title.toLowerCase();
  if (/договор|акт|шаблон|приказ/.test(low)) {
    return { icon: <FilePdfOutlined />, label: 'Документ · PDF', color: '#E53935' };
  }
  if (/брендбук|вывеска|логотип|меню/.test(low)) {
    return { icon: <FileImageOutlined />, label: 'Изображение · PDF', color: '#0096FF' };
  }
  if (/чек-лист|таблиц|расчёт/.test(low)) {
    return { icon: <FileExcelOutlined />, label: 'Таблица · XLSX', color: '#7CB342' };
  }
  return { icon: <FileTextOutlined />, label: 'Документ · MD', color: '#251D4A' };
}

// ─── УК видит ──────────────────────────────────────────────────────

function UkSeesCard({
  progress,
  overdueDays,
}: {
  progress: MyOpeningProgressDto;
  overdueDays: number;
}): JSX.Element {
  const isOnTrack = progress.daysLeft !== null && progress.daysLeft >= 0;

  return (
    <div
      style={{
        background:
          'linear-gradient(135deg, rgba(37, 29, 74, 0.04) 0%, rgba(255, 105, 0, 0.04) 100%)',
        border: '1px solid rgba(37, 29, 74, 0.18)',
        borderRadius: 8,
        padding: 20,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 6,
            background: 'rgba(37, 29, 74, 0.10)',
            color: 'var(--fk-indigo)',
            display: 'grid',
            placeItems: 'center',
          }}
        >
          <HomeOutlined />
        </div>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--fk-indigo)' }}>
          УК видит
        </div>
      </div>
      <div style={{ fontSize: 13, color: 'var(--fk-text-2)' }}>
        Прогресс открытия отображается в дашборде УК. Куратор подключится, если
        что-то застрянет.
      </div>
      {!isOnTrack && (
        <div
          style={{
            padding: 10,
            background: 'rgba(229, 57, 53, 0.06)',
            border: '1px solid rgba(229, 57, 53, 0.15)',
            borderRadius: 8,
          }}
        >
          <div style={{ fontSize: 12, color: 'var(--fk-error)', fontWeight: 600 }}>
            Шаг {progress.currentStepIndex} из {progress.totalSteps}
          </div>
          <div style={{ fontSize: 12, color: 'var(--fk-error)' }}>
            Отставание: {overdueDays} {pluralizeDays(overdueDays)}
          </div>
        </div>
      )}
      {progress.ukNotes && (
        <div
          style={{
            padding: 10,
            background: 'var(--fk-bg)',
            border: '1px solid var(--fk-border)',
            borderRadius: 8,
            fontSize: 12,
          }}
        >
          <div style={{ fontWeight: 600, color: 'var(--fk-text-2)', marginBottom: 4 }}>
            Заметка УК:
          </div>
          {progress.ukNotes}
        </div>
      )}
    </div>
  );
}

// ─── helpers ──────────────────────────────────────────────────────

function pluralizeDays(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'день';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return 'дня';
  return 'дней';
}
